import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { ChevronRight, ShoppingBag, RotateCcw, ArrowLeft } from 'lucide-react';
import { db } from '../src/firebase';
import { useUser } from '../contexts/UserContext';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../src/contexts/SettingsContext';
import { t } from '../translations';
import type { Order } from '../types';
import { toast } from 'sonner';

/**
 * История заказов анонимного гостя. Запрашивает заказы где
 * userId == current anonymous uid (по firestore.rules read разрешён).
 * Лимит 30 — на дольше историю не держим в UI.
 */
const OrdersHistory: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const { addToCart } = useCart();
  const { language } = useLanguage();
  const { settings } = useSettings();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userLoading || !user) return;
    const q = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Order)));
      setLoading(false);
    }, (err) => {
      console.error('orders history:', err?.message || 'unknown');
      setLoading(false);
    });
    return () => unsub();
  }, [user, userLoading]);

  const formatDate = (ts: any): string => {
    if (!ts) return '';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : null);
    return d ? d.toLocaleDateString(language === 'ru' ? 'ru-RU' : language, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  };

  const repeat = (order: Order) => {
    let added = 0;
    order.items.forEach((item: any) => {
      const cleaned = { ...item };
      delete cleaned.quantity;
      for (let i = 0; i < (item.quantity || 1); i++) {
        addToCart(cleaned, item.selectedAddons);
        added++;
      }
    });
    toast.success(`+${added} ${t('history.repeated', language)}`);
    navigate('/');
  };

  const statusColor = (s: string) => {
    switch (s) {
      case 'completed': return 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]';
      case 'cancelled': return 'bg-[var(--color-danger-bg)] text-[var(--color-danger-fg)]';
      case 'ready': return 'bg-[var(--color-role-bg)] text-[var(--color-role-fg)]';
      case 'accepted': return 'bg-[var(--color-info-bg)] text-[var(--color-info-fg)]';
      default: return 'bg-[var(--accent-10)] text-[var(--accent-color)]';
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] p-4 md:p-6">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 mb-4 text-sm font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('checkout.backToMenu', language)}
        </button>

        <h1 className="text-2xl font-black text-[var(--text-primary)] mb-1">
          {t('history.title', language)}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          {orders.length} {t('history.entries', language)}
        </p>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-[var(--accent-color)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-[var(--bg-card)] rounded-3xl border-2 border-dashed border-[var(--glass-border)] p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[var(--accent-10)] flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="w-10 h-10 text-[var(--accent-color)]" />
            </div>
            <h3 className="text-lg font-bold text-[var(--text-primary)] mb-1">
              {t('history.emptyTitle', language)}
            </h3>
            <p className="text-sm text-[var(--text-secondary)] mb-6">
              {t('history.emptySubtitle', language)}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-[var(--accent-color)] text-white rounded-2xl font-bold text-sm shadow-md"
            >
              {t('checkout.backToMenu', language)}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order, i) => (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-[var(--bg-card)] rounded-2xl border border-[var(--glass-border)] overflow-hidden"
              >
                <button
                  onClick={() => navigate(`/order/${order.id}`)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--neutral-bg)] transition-colors text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        #{order.orderNumber || order.id.slice(-4)}
                      </span>
                      <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${statusColor(order.status)}`}>
                        {t(`cashier.status.${order.status}`, language)}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-[var(--text-primary)] truncate">
                      {order.items.map((it: any) => it.names?.[language] || it.names?.ru).filter(Boolean).slice(0, 3).join(', ')}
                      {order.items.length > 3 && ` +${order.items.length - 3}`}
                    </p>
                    <p className="text-xs text-[var(--text-muted)] mt-0.5 tabular-nums">
                      {formatDate(order.createdAt)} · {order.total} {settings.currency}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--text-muted)] flex-shrink-0 ml-3" />
                </button>
                <div className="px-4 pb-3 flex gap-2">
                  <button
                    onClick={() => repeat(order)}
                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-[var(--accent-10)] text-[var(--accent-color)] hover:bg-[var(--accent-15)] transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t('history.repeat', language)}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersHistory;
